import React, {useState, useEffect} from 'react'
import {useDisclosure} from '@chakra-ui/react'
import {useToast} from '../../hooks/use-toast'
import useMultiSite from '../../hooks/use-multi-site'
import useShipToCountry from '../../hooks/use-ship-to-country'
import {useIntl} from 'react-intl'
import {TURKEY_PARTNER_SITE, RUSSIA_PARTNER_SITE, HOME_HREF} from '../../constants'
import {
    getShipToCountry,
    sortShipToCountries,
    getDefaultShipToCountry,
    getRedirectLanguage
} from '../../utils/ship-to-country-utils'
import {
    useShopperContext,
    useShopperContextsMutation,
    useUsid,
    useCommerceApi,
    useAccessToken,
    useCustomerType
} from '@salesforce/commerce-sdk-react'
import {
    getLocalStorageJSONItem,
    clearLocalStorageJSONItem,
    setLocalStorageJSONItem
} from '../../utils/utils'
import {useHistory, useLocation} from 'react-router-dom'

import {isServer} from '@salesforce/retail-react-app/app/utils/utils'

// Custom Hooks
import useSiteType from '../../hooks/use-site-type'

// Custom Components
import CountrySelectorModalContent from '../country-selector-modal-content'

// OOTB utils
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Custom API
import BasketMgr from '../../custom-api/basket-custom-api'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

/**
 * CountrySelectorModal is a React functional component that provides a modal interface for users to select and change their country.
 *
 * This component utilizes various hooks and context providers to manage the state and behavior of the modal. It fetches the user's
 * IP address to determine their geolocation and suggests a country based on this information. Users can confirm the suggested country
 * or select a different one from a dropdown list. Upon confirmation, the shopper context is updated with the selected country, and
 * appropriate actions such as redirection or local storage updates are performed.
 *
 * The modal is designed to be responsive and user-friendly, providing feedback through toast notifications in case of errors or
 * successful operations. It also handles specific redirection cases for certain countries.
 *
 * @component
 * @returns {JSX.Element} A modal component that allows users to change their country.
 *
 * @example
 * <CountrySelectorModal />
 *
 * @description
 * - The component uses the `useDisclosure` hook to manage the modal's open and close states.
 * - It fetches the user's IP address and geolocation using an asynchronous function `getIPAndOpenModal`.
 * - The `handleCountryChange` function updates the selected country code based on user input.
 * - The `handleConfirm` function updates the shopper context with the selected country and handles redirection if necessary.
 * - The `setShopperContextCountry` function sets the shopper context country and currency, either updating an existing context or creating a new one.
 * - The component sorts and displays a list of countries for selection.
 * - It uses the `useEffect` hook to determine whether geolocation should be checked and performs necessary actions based on the country code.
 */
// eslint-disable-next-line react/prop-types
const CountrySelectorModal = ({setIsSetShipToCountryEnabled}) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [geolocCountry, setGeolocCountry] = useState()
    const [geolocCountryCode, setGeolocCountryCode] = useState()
    const [selectedCountryCode, setSelectedCountryCode] = useState()
    const [isLoading, setIsLoading] = useState(false)
    const toast = useToast()
    const {site, locale, buildUrl, isOutletSite} = useMultiSite()
    const {shipToCountry, setShipToCountry} = useShipToCountry()
    const createShopperContext = useShopperContextsMutation('createShopperContext')
    const updateShopperContext = useShopperContextsMutation('updateShopperContext')
    const history = useHistory()
    const location = useLocation()
    const {isRegistered} = useCustomerType()
    const {isUS} = useSiteType()
    const {app: appConfig} = getConfig()
    const {triggerPopinChangeShipToEvent} = useGTM()
    const intl = useIntl()

    const basketMgr = new BasketMgr({
        parameters: {
            siteId: site.id,
            organizationId: appConfig.commerceAPI.parameters.organizationId,
            locale: locale.id
        }
    })

    const {usid} = useUsid()
    const {
        data: shopperContext,
        isSuccess,
        isLoading: isShopperContextLoading
    } = useShopperContext(
        {
            parameters: {usid: usid}
        },
        {enabled: Boolean(!isServer && usid)}
    )

    // Control Modal
    useEffect(() => {
        if (!isShopperContextLoading) {
            setIsSetShipToCountryEnabled(
                Boolean(
                    (shopperContext?.geoLocation?.countryCode ||
                        shopperContext?.geoLocation?.country) &&
                        shopperContext?.customQualifiers?.locale &&
                        shopperContext?.customQualifiers?.currency
                )
            )
        }
    }, [isShopperContextLoading, shopperContext, setIsSetShipToCountryEnabled])

    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()

    const urlParams = new URLSearchParams(location.search)
    // If the URL contains a country query parameter, it will be used to set the shopper context country.
    const country = urlParams.get('country')
    /**
     * Asynchronously retrieves the user's IP address and opens a modal with country selection options based on the IP-derived location.
     *
     * This function performs the following steps:
     * 1. Fetches the user's IP address from the '/ip' endpoint.
     * 2. Uses the IP address to create a shopper context, which determines the user's geographical location.
     * 3. Parses the geolocation data from the response headers.
     * 4. Sets the country and country code based on the geolocation data.
     * 5. Opens a modal to allow the user to select their country.
     *
     * If any step fails, an error message is logged to the console, and a toast notification is displayed to inform the user of the failure.
     *
     * @async
     * @function
     * @throws Will throw an error if the IP address cannot be retrieved or if the shopper context cannot be updated.
     */
    const getIPAndOpenModal = async () => {
        try {
            // Get IP address of the user
            const response = await fetch('/ip', {
                method: 'GET'
            })

            const result = await response.json()
            const userIP = result.clientIp // example IP: '212.58.75.254'

            if (!userIP) {
                throw new Error('Failed to retrieve user IP address')
            }

            // Create a shopper context to retrieve country according to user's IP
            const token = await getTokenWhenReady()
            const contextParams = {
                parameters: {
                    usid: usid
                },
                headers: {
                    authorization: `Bearer ${token}`
                },
                body: {
                    clientIp: userIP
                }
            }
            const resp = await api.shopperContexts.createShopperContext(contextParams, true)
            const geoLocation = resp?.headers?.get('x-geolocation')

            // Set variables for modal display and open it
            if (geoLocation) {
                const geoData = geoLocation.split('; ').reduce((acc, item) => {
                    const [key, value] = item.split(': ')
                    acc[key] = value
                    return acc
                }, {})
                setGeolocCountry(geoData['Country'])
                setGeolocCountryCode(geoData['CountryCode'])
                setSelectedCountryCode(geoData['CountryCode'])
                // open modal if the geoLocation country is different from the current shipToCountry
                if (shipToCountry?.id != geoData['CountryCode']) {
                    onOpen()
                    window.dataLayer.push({event: 'shipto_modal_view'})
                } else {
                    // if geoLocation is the same as the current shipToCountry, set the shopper context country to the geoLocation country
                    const geoLocationShipToCountry = getShipToCountry(geoData['CountryCode'], isUS)
                    await setShopperContextCountry(geoLocationShipToCountry)
                }
            } else {
                // no geoLocation was returned, so set the default country based on the site type
                await setDefaultShipToCountry()
            }
        } catch (error) {
            // error getting user location
            // set default ship to country
            await setDefaultShipToCountry()
            toast({
                title: 'Error fetching user location.',
                description: error.message,
                status: 'error'
            })
        }
    }

    /**
     * Handles the change event for the country selection dropdown.
     *
     * This function is triggered when the user selects a different country from the dropdown menu.
     * It updates the state with the newly selected country's code.
     *
     * @param {Object} event - The event object associated with the change event.
     * @param {Object} event.target - The target element of the event.
     * @param {string} event.target.value - The value of the selected country code.
     */
    const handleCountryChange = (event) => {
        setSelectedCountryCode(event.target.value)
    }

    /**
     * Handles the confirmation action for the country selection modal.
     *
     * This function manages the redirection and context setting based on the selected country code.
     * It performs the following actions:
     *
     * - Sets the loading state to true at the beginning of the process.
     * - Redirects the user to the appropriate site based on the selected country code and current site alias:
     *   - If the selected country is the United States ('US') and the current site alias is 'eu', redirects to the US site.
     *   - If the selected country is not the United States and the current site alias is 'us', redirects to the EU site.
     *   - If the selected country is Turkey ('TR'), redirects to the Turkey partner site.
     *   - If the selected country is Russia ('RU'), redirects to the Russia partner site.
     * - For other countries, attempts to update the shopper context and ship-to country:
     *   - Calls `getShipToCountry` to determine the shipping country.
     *   - Updates the shopper context with `setShopperContextCountry`.
     *   - Sets the ship-to country state.
     *   - Displays a success toast notification upon successful update.
     *   - Closes the modal using `onClose`.
     * - Catches and handles errors during the update process:
     *   - Displays an error toast notification with the error message.
     * - Resets the loading state to false at the end of the process.
     *
     * @async
     * @function handleConfirm
     */
    const handleConfirm = async (countryCode) => {
        let auxOldShipToCountry = shopperContext?.geoLocation?.countryCode
        setIsLoading(true)
        // Handle redirection cases
        const finalCountryCode = countryCode || selectedCountryCode
        const preferredLocale = getRedirectLanguage(finalCountryCode, locale?.id)
        if (finalCountryCode === 'US' && ['eu', 'outlet-eu'].includes(site.alias)) {
            // GTM Integration
            triggerPopinChangeShipToEvent(auxOldShipToCountry, finalCountryCode)

            window.location.href = buildUrl(
                HOME_HREF + `?country=${finalCountryCode}`,
                isOutletSite ? 'outlet-us' : 'us',
                preferredLocale
            )
        } else if (
            finalCountryCode !== 'US' &&
            finalCountryCode !== 'TR' &&
            finalCountryCode !== 'RU' &&
            ['us', 'outlet-us'].includes(site.alias)
        ) {
            // GTM Integration
            triggerPopinChangeShipToEvent(auxOldShipToCountry, finalCountryCode)

            window.location.href = buildUrl(
                HOME_HREF + `?country=${finalCountryCode}`,
                isOutletSite ? 'outlet-eu' : 'eu',
                preferredLocale
            )
        } else if (finalCountryCode === 'TR') {
            // GTM Integration
            triggerPopinChangeShipToEvent(auxOldShipToCountry, finalCountryCode)

            window.location.href = TURKEY_PARTNER_SITE
        } else if (finalCountryCode === 'RU') {
            // GTM Integration
            triggerPopinChangeShipToEvent(auxOldShipToCountry, finalCountryCode)

            window.location.href = RUSSIA_PARTNER_SITE
        } else {
            // Handle other cases
            try {
                const selectedShipToCountry = getShipToCountry(finalCountryCode, isUS)

                await setShopperContextCountry(selectedShipToCountry)
                setShipToCountry(selectedShipToCountry)
                setIsSetShipToCountryEnabled(true)
                toast({
                    title: 'Country updated successfully.',
                    description: '',
                    status: 'success'
                })
                onClose()

                // GTM Integration
                triggerPopinChangeShipToEvent(auxOldShipToCountry, finalCountryCode)
            } catch (error) {
                toast({
                    title: 'An error occurred.',
                    description: error.message,
                    status: 'error'
                })
            }
        }
        setIsLoading(false)
    }

    /**
     * Sets the shopper context country and currency.
     * Updates an existing shopper context if it already exists, otherwise creates a new shopper context.
     * @param {object} countryAndCurrency - The object containing the country and currency to set
     */
    const setShopperContextCountry = async ({id, currency}) => {
        let customQualifiers = {}
        if (shopperContext && shopperContext.customQualifiers) {
            customQualifiers = shopperContext.customQualifiers || {}
        }
        Object.assign(customQualifiers, {
            currency: currency,
            locale: locale?.id
        })
        const options = {
            parameters: {
                usid: usid,
                siteId: site.id
            },
            body: {
                geoLocation: {
                    countryCode: id
                },
                customQualifiers: customQualifiers
            }
        }

        if (shopperContext?.data) {
            await updateShopperContext.mutateAsync(options)
        } else {
            await createShopperContext.mutateAsync(options)
        }

        // update the basket assignments with the new country
        const accessToken = await getTokenWhenReady()
        await basketMgr.sfsBasketAssignments(accessToken, site.id, locale.id)
    }

    /**
     * Sets the default shipping country for the user.
     *
     * This asynchronous function determines the default shipping country based on
     * whether the user is in the United States. It then updates the local storage
     * with the country code, sets the shipping country state, and enables the
     * shipping country setting. Finally, it updates the shopper context with the
     * selected country.
     *
     * @async
     * @function setDefaultShipToCountry
     * @returns {Promise<void>} A promise that resolves when the shopper context
     *          country has been successfully set.
     */
    const setDefaultShipToCountry = async () => {
        const defaultShipToCountry = getDefaultShipToCountry(isUS)
        setLocalStorageJSONItem('countryCode', defaultShipToCountry?.id)
        setShipToCountry(defaultShipToCountry)
        setIsSetShipToCountryEnabled(true)
        await setShopperContextCountry(defaultShipToCountry)
    }

    // Concatenate and sort countries
    const countries = sortShipToCountries(intl)

    useEffect(() => {
        const newCountryCode = getLocalStorageJSONItem('countryCode') || country
        /**
         * Determines whether geolocation should be checked and performs necessary actions based on the country code.
         *
         * This asynchronous function checks if a new country code is provided and updates the shopper context
         * and local storage accordingly. If no new country code is provided, it evaluates the success status
         * and loading state of the shopper context to decide whether to retrieve the IP address and open a modal.
         *
         * @async
         * @function shouldCheckGeolocation
         * @returns {Promise<void>} A promise that resolves when the necessary actions are completed.
         *
         * @description
         * - If a `newCountryCode` is available, the function retrieves the corresponding shipping country,
         *   updates the shopper context with this country, sets the shipping country, and clears the local
         *   storage item for 'countryCode'.
         * - If no `newCountryCode` is provided, the function checks the success and loading status of the
         *   shopper context. If the context is not successful and not loading, or if it is successful but
         *   lacks a geolocation country code, it triggers the retrieval of the IP address and opens a modal.
         */
        async function shouldCheckGeolocation() {
            const isNotSuccessfulAndNotLoading = !isSuccess && !isShopperContextLoading

            const shouldOpenIPModal =
                isNotSuccessfulAndNotLoading &&
                !shopperContext?.geoLocation?.countryCode &&
                !shopperContext?.geoLocation?.country

            const hasShopperContext =
                shopperContext?.geoLocation?.countryCode ||
                (shopperContext?.geoLocation?.country && shopperContext?.customQualifiers?.currency)
            const localeMismatch = shopperContext?.customQualifiers?.locale !== locale?.id
            const shouldUpdateLocale =
                hasShopperContext && (!shopperContext?.customQualifiers?.locale || localeMismatch)

            if (newCountryCode && !isShopperContextLoading) {
                let finalShipToCountry = getShipToCountry(newCountryCode, isUS)
                // check if the selected country is different from the current site
                if (
                    (newCountryCode === 'US' && site.alias?.endsWith('eu')) ||
                    (newCountryCode !== 'US' && site.alias?.endsWith('us'))
                ) {
                    // country not supported, get default
                    finalShipToCountry = getDefaultShipToCountry(isUS)
                }

                await setShopperContextCountry(finalShipToCountry)
                setShipToCountry(finalShipToCountry)
                clearLocalStorageJSONItem('countryCode')
                setIsSetShipToCountryEnabled(true)
                // Remove the country query parameter from the URL after processing it.
                urlParams.delete('country')
                history.replace({
                    pathname: location.pathname,
                    search: urlParams.toString()
                })
            } else if (shouldOpenIPModal) {
                getIPAndOpenModal()
            } else if (isNotSuccessfulAndNotLoading || (isSuccess && shouldUpdateLocale)) {
                let countryFromShopperContext = {
                    id:
                        shopperContext?.geoLocation?.countryCode ||
                        shopperContext?.geoLocation?.country,
                    currency: shopperContext?.customQualifiers?.currency
                }
                await setShopperContextCountry(countryFromShopperContext)
            }
        }

        shouldCheckGeolocation()
    }, [isShopperContextLoading, isRegistered])

    return (
        <CountrySelectorModalContent
            isOpen={isOpen}
            onClose={() => {}}
            geolocCountryCode={geolocCountryCode}
            shipToCountryCode={shipToCountry?.id || ''}
            countries={countries}
            handleCountryChange={handleCountryChange}
            setSelectedCountryCode={setSelectedCountryCode}
            handleConfirm={handleConfirm}
            isLoading={isLoading}
            isCheckout={false}
        />
    )
}

export default CountrySelectorModal
